import { ChangeEvent, useMemo, useState, useRef, useEffect } from 'react';
import { Box, Center, Checkbox, Text, useDisclosure, useToast } from '@chakra-ui/react';
import {
    Column,
    Row,
    SortByFn,
    useAsyncDebounce,
    useFilters,
    useGlobalFilter,
    usePagination,
    useSortBy,
    useTable,
} from 'react-table';
import FileSaver from 'file-saver';

import { linkStyle } from 'shared/styles';
import { useGetClientKeywordsQuery } from 'core/API/keywordsApi';
import { useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import {
    IProductKeyword,
    IVisibleProductKeyword,
    IVisibleProductKeywordByProduct,
    IProductKeywordByProduct,
    TProductKeywordQueryType,
    ISortableProductKeywordColumns,
    TPKServerSortTypes,
    IUserInfo,
} from 'types';
import { sortProductKeywords } from 'plugins/DITable/helpers/sort';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import {
    useAddProductKeywordsMutation,
    useDeleteProductKeywordsMutation,
    useGetProductKeywordsByProductQuery,
    useLazyExportProductKeywordsQuery,
    useUpdateProductKeywordsMutation,
} from 'core/API/productKeywordsApi';
import { EMPTY_PRODUCT_KEYWORDS, KEYWORD_COLUMN_LIMIT } from './constants';
import { EXCEL_FILE_TYPE } from 'shared/const';
import { useGetRetailerByClientQuery } from 'core/API/authApi';

const useProductKeywordsContainer = ({ readonly, userInfo }: { readonly: boolean; userInfo?: IUserInfo }) => {
    const [productKeywordToAdd, setProductKeywordToAdd] = useState(EMPTY_PRODUCT_KEYWORDS);
    const [productKeywordToUpdate, setProductKeywordToUpdate] = useState<IVisibleProductKeywordByProduct | null>(null);
    const [modalType, setModalType] = useState<'add' | 'update'>('add');
    const [selectedProductKeywords, setSelectedProductKeywords] = useState<string[]>([]);
    const [isDeleteAll, setIsDeleteAll] = useState(false); // Is it used ? @Amine
    const [productKeywordsCount, setProductKeywordsCount] = useState(0);
    const [globalFilter, setGlobalFilter] = useState('');
    const [eanFilter, setEanFilter] = useState('');
    const [keywordFilter, setKeywordFilter] = useState('');
    const [priorityKeywordFilter, setPriorityKeywordFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchBy, setSearchBy] = useState<TProductKeywordQueryType>('global');
    const [sortBy, setSortBy] = useState<keyof ISortableProductKeywordColumns | undefined>(undefined);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | undefined>(undefined);
    const [sortableColumns, setSortableColumns] = useState<ISortableProductKeywordColumns>({
        ean: 'default',
    });
    const [languages, setLanguages] = useState<string[]>([]);
    const { data: retailers, isLoading: isRetailersLoading, isError: isRetailerError } = useGetRetailerByClientQuery();

    const [exportProductKeywords, { isLoading: isExportLoading, isFetching }] = useLazyExportProductKeywordsQuery();

    const [pageIndex, setPageIndex] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const queryParams = {
        page: pageIndex + 1,
        limit: rowsPerPage,
        search: searchQuery,
        searchBy,
        sort: sortDirection,
        sortBy,
    };
    const { data: productKeywordsData, isError, isLoading } = useGetProductKeywordsByProductQuery(queryParams);
    const { data: keywords, isLoading: isKeywordsLoading, isError: isKeywordsError } = useGetClientKeywordsQuery();
    const [changingPage, setChangingPage] = useState(false);
    const [addProductKeywords, { isLoading: isAddLoading }] = useAddProductKeywordsMutation();
    const [updateProductKeywords, { isLoading: isUpdateLoading }] = useUpdateProductKeywordsMutation();
    const [deleteProductKeywords, { isLoading: isDeleteLoading }] = useDeleteProductKeywordsMutation();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const translate = useTranslation();
    const toast = useToast();
    const upcUser = userInfo?.country.iso === 'US' || userInfo?.country.iso === 'CA';
    const updateInitialRef = useRef(null);
    const deleteInitialRef = useRef(null);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
    const [data, setData] = useState<IProductKeywordByProduct[]>([]);

    useEffect(() => {
        if (!isRetailersLoading && !isRetailerError && retailers) {
            const langs: Set<string> = new Set<string>();
            retailers.data.forEach((retailer) => {
                if (retailer.languages) {
                    retailer.languages.forEach((l: string) => {
                        if (l !== '') langs.add(l);
                    });
                }
            });

            const langsArr = Array.from(langs);
            setLanguages(langsArr);
        }
    }, [retailers, isRetailersLoading]);

    useEffect(() => {
        if (!isLoading && !isKeywordsLoading && !isError && productKeywordsData) {
            setProductKeywordsCount(productKeywordsData.total_product_keywords);
            if (keywords && keywords?.keywords.length > 0) {
                setProductKeywordToAdd({
                    ...productKeywordToAdd,
                    keywords: [
                        {
                            keyword_id: undefined,
                            is_priority: false,
                        },
                    ],
                });
            }
            setChangingPage(false);
            setData(
                productKeywordsData.product_keywords.map((pk) => {
                    return {
                        ean: pk.ean,
                        upc: upcUser ? pk.upc : undefined,
                        keywords: pk.keywords.map((k) => {
                            const keyword = keywords?.keywords.find(
                                (fullKeyword) => fullKeyword.keyword_id === k.keyword_id,
                            );

                            if (keyword) {
                                return {
                                    ...k,
                                    name: keyword.name,
                                    language: keyword.language,
                                };
                            }
                            return k;
                        }),
                    };
                }),
            );
        }
    }, [isLoading, isKeywordsLoading, isError, productKeywordsData]);

    const globalFilterHandler = useAsyncDebounce((value: string) => {
        setGlobalFilter(value);
        setChangingPage(true);
        if (value === searchQuery && searchBy === 'global') return;
        setSearchBy('global');
        setSearchQuery(value);
    }, 500);

    const handleEditProductKeyword = (productKeyword: IProductKeywordByProduct) => {
        setModalType('update');
        setProductKeywordToUpdate(productKeyword);
        onOpen();
    };

    const caseInsensitiveSort = useMemo(() => sortProductKeywords, []);

    const sortTypes: Record<string, SortByFn<any>> = useMemo(
        () => ({
            caseInsensitive: caseInsensitiveSort,
        }),
        [],
    );

    const handleCheckRow = (event: ChangeEvent<HTMLInputElement>, ean: string) => {
        if (event.target.checked) setSelectedProductKeywords([...selectedProductKeywords, ean]);
        else setSelectedProductKeywords(selectedProductKeywords.filter((e) => e !== ean));
    };

    const handleCheckAllRows = () => {
        if (productKeywordsData) {
            if (selectedProductKeywords.length < productKeywordsData.product_keywords.length)
                setSelectedProductKeywords(productKeywordsData.product_keywords.map((pk) => pk.ean));
            else setSelectedProductKeywords([]);
        } else setSelectedProductKeywords([]);
    };

    const columns: Readonly<Column[]> = useMemo(() => {
        const nbKeywords = productKeywordsData ? productKeywordsData.product_keywords.length : 1;
        const tableColumns: readonly Column[] = [
            ...(readonly
                ? []
                : [
                      {
                          Header: () => (
                              <Center m={0}>
                                  <Checkbox
                                      onChange={handleCheckAllRows}
                                      isIndeterminate={
                                          selectedProductKeywords.length > 0 &&
                                          selectedProductKeywords.length < nbKeywords
                                      }
                                      isChecked={nbKeywords > 0 && selectedProductKeywords.length === nbKeywords}
                                  ></Checkbox>
                              </Center>
                          ),
                          id: 'selector',
                          disableFilters: true,
                          disableGlobalFilter: true,
                          Cell: ({
                              cell: {
                                  row: { original },
                              },
                          }) => {
                              const productKeywordOriginal = original as IProductKeyword;
                              return (
                                  <Center>
                                      <Checkbox
                                          onChange={(e) => {
                                              handleCheckRow(e, productKeywordOriginal.ean);
                                          }}
                                          isChecked={selectedProductKeywords.includes(productKeywordOriginal.ean)}
                                      ></Checkbox>
                                  </Center>
                              );
                          },
                      } as Column,
                  ]),
            {
                Header: translate('product_keyword.column.ean') as string,
                accessor: 'ean',
                sortType: 'caseInsensitive',
                disableFilters: true,
                disableGlobalFilter: true,
                width: '30%',
                Cell: ({ cell: { row } }) => {
                    const productKeywordsRow = row.original as IProductKeywordByProduct;
                    return (
                        <Box
                            onClick={() => {
                                handleEditProductKeyword(productKeywordsRow);
                            }}
                        >
                            <Text {...linkStyle}>
                                {upcUser && productKeywordsRow.upc ? productKeywordsRow.upc : row.values.ean}
                            </Text>
                        </Box>
                    );
                },
            },
            {
                Header: translate('product_keyword.column.kewyords') as string,
                accessor: 'keywords',
                sortType: 'caseInsensitive',
                disableFilters: true,
                disableGlobalFilter: true,
                width: '35%',
                filter: (rows: Row[], columnIds: string[], filterValue: string) => {
                    return rows?.filter((row) => {
                        const rowToFilter = row.original as IProductKeywordByProduct;
                        const filteredKeywords = rowToFilter.keywords?.filter((k) => !k.is_priority && k.name);
                        return filteredKeywords?.some((k) =>
                            k.name?.toLowerCase().includes(filterValue?.toLowerCase()),
                        );
                    });
                },
                Cell: ({ cell: { row } }) => {
                    const productKeywordValue = row.values as IProductKeywordByProduct;
                    const nonPriorityKeywords = productKeywordValue.keywords?.filter((k) => !k.is_priority);
                    const additionalKeywordsNum = nonPriorityKeywords.length - KEYWORD_COLUMN_LIMIT;
                    return (
                        <Box>
                            <Text>
                                {nonPriorityKeywords.length ? (
                                    <>
                                        {nonPriorityKeywords
                                            .slice(0, KEYWORD_COLUMN_LIMIT)
                                            .map((keyword) => keyword?.name)
                                            .join(', ')}
                                        {additionalKeywordsNum > 0 && (
                                            <Text as="span">
                                                {`... (${additionalKeywordsNum} ${(
                                                    (additionalKeywordsNum > 1
                                                        ? translate('product_keyword.column.kewyords')
                                                        : translate('product_keyword.column.kewyord')) as string
                                                ).toLocaleLowerCase()})`}
                                            </Text>
                                        )}
                                    </>
                                ) : (
                                    <Text as="span">-</Text>
                                )}
                            </Text>
                        </Box>
                    );
                },
            },

            {
                Header: translate('product_keyword.column.priority_kewyords') as string,
                accessor: 'priority_keywords',
                sortType: 'caseInsensitive',
                disableFilters: true,
                disableGlobalFilter: true,
                width: '35%',
                filter: (rows: Row[], columnIds: string[], filterValue: string) => {
                    return rows?.filter((row) => {
                        const rowToFilter = row.original as IProductKeywordByProduct;
                        const filteredKeywords = rowToFilter.keywords?.filter((k) => k.is_priority && k.name);
                        return filteredKeywords?.some((k) =>
                            k.name?.toLowerCase().includes(filterValue?.toLowerCase()),
                        );
                    });
                },
                Cell: ({ cell: { row } }) => {
                    const productKeywordValue = row.values as IProductKeywordByProduct;
                    const priorityKeywords = productKeywordValue.keywords?.filter((k) => k.is_priority);
                    const additionalKeywordsNum = priorityKeywords.length - KEYWORD_COLUMN_LIMIT;

                    return (
                        <Box>
                            <Text>
                                {priorityKeywords.length > 0 ? (
                                    <>
                                        {priorityKeywords
                                            .slice(0, KEYWORD_COLUMN_LIMIT)
                                            .map((keyword) => keyword?.name)
                                            .join(', ')}
                                        {additionalKeywordsNum > 0 && (
                                            <Text as="span">
                                                {`... (${additionalKeywordsNum} ${(
                                                    (additionalKeywordsNum > 1
                                                        ? translate('product_keyword.column.kewyords')
                                                        : translate('product_keyword.column.kewyord')) as string
                                                ).toLocaleLowerCase()})`}
                                            </Text>
                                        )}
                                    </>
                                ) : (
                                    <Text as="span">-</Text>
                                )}
                            </Text>
                        </Box>
                    );
                },
            },
        ];
        const filteredColumns = tableColumns.filter((e): e is Column => e !== null);
        return filteredColumns;
    }, [selectedProductKeywords, productKeywordsData, userInfo, readonly]);

    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        [],
    );

    const tableInstance = useTable(
        {
            columns,
            globalFilter: (rows: Row[], columnsIds: string[], filterValue: string) => {
                return rows;
            },
            data,
            defaultColumn,
            sortTypes,
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    tableInstance.state.pageSize = rowsPerPage;

    const handleAddProductKeywordOpen = () => {
        setModalType('add');
        onOpen();
    };

    const handleCloseAddAndUpdateModal = () => {
        if (modalType === 'add')
            setProductKeywordToAdd({
                ean: '',
                upc: undefined,
                keywords: [
                    {
                        keyword_id: undefined,
                        is_priority: false,
                    },
                ],
            });
        onClose();
    };

    const handleChangeProductKeyword = (eventValue: any, type: keyof IVisibleProductKeyword, id?: number) => {
        switch (type) {
            case 'is_priority':
                if (modalType === 'add')
                    setProductKeywordToAdd((prev) => ({
                        ...prev,
                        keywords: prev.keywords.map((k) => {
                            if (k.keyword_id === id) {
                                return {
                                    ...k,
                                    [type]: !k[type],
                                };
                            }
                            return k;
                        }),
                    }));
                else {
                    setProductKeywordToUpdate((prev) => ({
                        ...prev!,
                        keywords: prev!.keywords.map((k) => {
                            if (k.keyword_id === id) {
                                return {
                                    ...k,
                                    [type]: !k[type],
                                };
                            }
                            return k;
                        }),
                    }));
                }

                break;
            case 'keyword_id':
                if (modalType === 'add')
                    setProductKeywordToAdd((prev) => ({
                        ...prev,
                        keywords: prev.keywords.map((k) => {
                            if (k.keyword_id === id) {
                                return {
                                    ...k,
                                    [type]: Number(eventValue) ? Number(eventValue) : undefined,
                                };
                            }
                            return k;
                        }),
                    }));
                else
                    setProductKeywordToUpdate((prev) => ({
                        ...prev!,
                        keywords: prev!.keywords.map((k) => {
                            if (k.keyword_id === id) {
                                return {
                                    ...k,
                                    [type]: Number(eventValue) ? Number(eventValue) : undefined,
                                };
                            }
                            return k;
                        }),
                    }));
                break;
            default:
                if (modalType === 'add') setProductKeywordToAdd({ ...productKeywordToAdd, [type]: eventValue });
                else {
                    setProductKeywordToUpdate((prev) => ({
                        ...prev!,
                        keywords: prev!.keywords.map((k) => {
                            if (k.keyword_id === id) {
                                return {
                                    ...k,
                                    [type]: eventValue,
                                };
                            }
                            return k;
                        }),
                    }));
                }
        }
    };

    const handleDeleteKeyword = (id: number) => {
        if (modalType === 'add')
            setProductKeywordToAdd((prev) => ({
                ...prev,
                keywords: prev.keywords.filter((k) => k.keyword_id !== id),
            }));
        else
            setProductKeywordToUpdate((prev) => ({
                ...prev!,
                keywords: prev!.keywords.filter((k, index) => k.keyword_id !== id),
            }));
    };

    const handleSubmitProductKeywords = async () => {
        const productKeywordsToSubmit = modalType === 'add' ? productKeywordToAdd : productKeywordToUpdate;
        try {
            const filteredProductKeywords = {
                ean: productKeywordsToSubmit?.ean,
                upc: upcUser ? productKeywordsToSubmit?.upc : undefined,
                keywords: productKeywordsToSubmit?.keywords.filter((k) => k.keyword_id !== undefined),
            };

            const res =
                modalType === 'add'
                    ? await addProductKeywords({
                          productKeywords: filteredProductKeywords as IVisibleProductKeywordByProduct,
                          getParams: queryParams,
                      })
                    : await updateProductKeywords({
                          productKeywords: filteredProductKeywords as IVisibleProductKeywordByProduct,
                          getParams: queryParams,
                      });

            handleCloseAddAndUpdateModal();

            if ('error' in res) {
                // @ts-ignore
                if (res?.error?.data?.errorCode === 'product_keyword.keyword.not_found') {
                    // @ts-ignore
                    throw new Error(translate(res.error.data.errorCode) as string);
                }
                throw new Error(translate('product_keyword.modal.updateError') as string);
            }
            const eanFound = res.data.ean_found;
            toast({
                title:
                    modalType === 'add'
                        ? translate(
                              eanFound ? 'product_keyword.add.success' : 'product_keyword.add.successEanNotDefined',
                              {
                                  ean: filteredProductKeywords.ean,
                              },
                          )
                        : translate('product_keyword.modal.updateSuccesful'),
                status: eanFound || modalType === 'update' ? 'success' : 'warning',

                isClosable: true,
            });
        } catch (error) {
            toast({
                title: (error as Error).message ? (error as Error).message : translate('error.generic'),
                status: 'error',
                isClosable: true,
            });
        }
    };

    const handleSort = (columnId?: string) => {
        const columnToSort = columnId as keyof TPKServerSortTypes;

        setSortableColumns((prev) => {
            let newValue = 'default';

            switch (prev[columnToSort]) {
                case 'default':
                    newValue = 'asc';
                    break;
                case 'asc':
                    newValue = 'desc';
                    break;
                default:
                    newValue = 'default';
                    break;
            }

            setSortBy(columnToSort !== 'default' ? columnToSort : undefined);
            setSortDirection(columnToSort !== 'default' ? (newValue as 'asc' | 'desc') : undefined);

            return {
                ...prev,
                [columnToSort]: newValue,
            };
        });

        setChangingPage(true);
    };

    const handleExport = async () => {
        try {
            const { data: res, isError: isExportError } = await exportProductKeywords({
                isTemplate: false,
            });
            if (isExportError || !res) throw new Error(translate('keyword.export.error') as string);

            const blob = new Blob([res], {
                type: EXCEL_FILE_TYPE,
            });

            FileSaver.saveAs(blob, translate('product_keyword.export.fileName') as string);
        } catch (error) {
            toast({
                title: (error as Error).message,
                status: 'error',
                isClosable: true,
            });
        }
    };

    const addKeywordToList = (type: 'add' | 'update') => {
        if (type === 'add')
            setProductKeywordToAdd((prev) => ({
                ...prev,
                keywords: [
                    ...prev.keywords,
                    {
                        ean: prev.ean,
                        keyword_id: undefined,
                        is_priority: false,
                    },
                ],
            }));
        else
            setProductKeywordToUpdate((prev) => ({
                ean: prev!.ean,
                upc: upcUser ? prev!.upc : undefined,
                keywords: [
                    ...prev!.keywords,
                    {
                        ean: prev!.ean,
                        upc: upcUser ? prev!.upc : undefined,
                        keyword_id: undefined,
                        is_priority: false,
                    },
                ],
            }));
    };

    useEffect(() => {
        if (!eanFilter || (eanFilter === searchQuery && searchBy === 'ean')) return;

        setSearchBy('ean');
        setSearchQuery(eanFilter);
        setChangingPage(true);
    }, [eanFilter]);

    useEffect(() => {
        if (!keywordFilter || (keywordFilter === searchQuery && searchBy === 'keywords')) return;

        setSearchBy('keywords');
        setSearchQuery(keywordFilter);
        setChangingPage(true);
    }, [keywordFilter]);

    useEffect(() => {
        if (!priorityKeywordFilter || (priorityKeywordFilter === searchQuery && searchBy === 'priority_keywords'))
            return;

        setSearchBy('priority_keywords');
        setSearchQuery(priorityKeywordFilter);
        setChangingPage(true);
    }, [priorityKeywordFilter]);

    const customFilters = [
        {
            id: 'ean',
            value: eanFilter,
            onChange: (value: string) => setEanFilter(value),
        },
        {
            id: 'keywords',
            value: keywordFilter,
            onChange: (value: string) => setKeywordFilter(value),
        },
        {
            id: 'priority_keywords',
            value: priorityKeywordFilter,
            onChange: (value: string) => setPriorityKeywordFilter(value),
        },
    ];

    const paginationTableInstance = {
        ...tableInstance,
        canNextPage: productKeywordsCount > rowsPerPage * (pageIndex + 1),
        canPreviousPage: pageIndex > 0,
        pageCount: Math.ceil(productKeywordsCount / rowsPerPage),
        nextPage: () => {
            if (productKeywordsCount > rowsPerPage * (pageIndex + 1)) {
                setChangingPage(true);
                setPageIndex(pageIndex + 1);
            }
        },
        previousPage: () => {
            if (pageIndex > 0) setPageIndex(pageIndex - 1);
        },
        setPageSize: (size: number) => {
            setRowsPerPage(size);
            setPageIndex(0);
            setChangingPage(true);
        },
        state: {
            ...tableInstance.state,
            pageIndex,
            pageSize: rowsPerPage,
        },
    };

    const props = useMemo(
        () => ({
            paginationTableInstance,
            customFilters,
            tableInstance,
            isLoading,
            changingPage,
            sortableColumns,
            rowsPerPage,
            globalFilter,
            selectedProductKeywords,
            queryParams,
            deleteInitialRef,
            isDeleteOpen,
            isDeleteLoading,
            modalType,
            productKeywordToAdd,
            productKeywordToUpdate,
            updateInitialRef,
            isOpen,
            isAddLoading,
            isUpdateLoading,
            keywords,
            isExportLoading,
            isFetching,
            languages,
            readonly,
            handleExport,
            addKeywordToList,
            setIsDeleteAll,
            onDeleteOpen,
            globalFilterHandler,
            deleteProductKeywords,
            onDeleteClose,
            setSelectedProductKeywords,
            handleSort,
            handleAddProductKeywordOpen,
            handleCloseAddAndUpdateModal,
            handleSubmitProductKeywords,
            handleChangeProductKeyword,
            handleDeleteKeyword,
        }),
        [
            languages,
            paginationTableInstance,
            customFilters,
            tableInstance,
            isLoading,
            changingPage,
            sortableColumns,
            rowsPerPage,
            globalFilter,
            selectedProductKeywords,
            queryParams,
            deleteInitialRef,
            isDeleteOpen,
            isDeleteLoading,
            modalType,
            productKeywordToAdd,
            productKeywordToUpdate,
            updateInitialRef,
            isOpen,
            isAddLoading,
            isUpdateLoading,
            keywords,
            isExportLoading,
            isFetching,
            readonly,
        ],
    );

    return props;
};

export type TProductKeywordsContainer = ReturnType<typeof useProductKeywordsContainer>;
export default useProductKeywordsContainer;
